import CoreImage from 'core/variables/images';
import React from 'react';
import Image from '../Image/Image';
import type { ImageProps } from '../Image/Image';

/**
 * AssetImage — v2 atom.
 *
 * Compone {@link Image} y resuelve un `id` del registro de assets del proyecto
 * (`core/variables/images`) — logos, covers, no-items, imágenes de signup, etc.
 * Si el `id` es desconocido, el registro cae por defecto a `logoBig` (paridad
 * con el v1).
 *
 * El registro es monkey-patched en runtime por el tenant activo (ver
 * `src/App.js`: importa `edelvives/utils/skinAssets/images` o
 * `app/utils/skinAssets/images` por sus efectos secundarios, que reasignan
 * `CoreImage.getImage`). En Storybook se ven los assets por defecto — los
 * swaps por tenant sólo ocurren en runtime de la app.
 *
 * Si se pasa `src`, gana sobre `id` (paridad con v1) — útil para overrides
 * puntuales sin salirse de este atom.
 */

export type AssetImageId =
  | 'logoBig'
  | 'logoIconS'
  | 'logoIconInverse'
  | 'noBooks'
  | 'noMembers'
  | 'noItem'
  | 'noQuestions'
  | 'videoContentPreview'
  | 'login'
  | 'signup1'
  | 'signup2'
  | 'signup3'
  | 'signup4'
  | 'signup5'
  | 'noUsers'
  | 'noPictureProgram'
  | 'noModuleCover'
  | 'noModuleCoverAdd'
  | 'cover-test-started'
  | 'cover-test-blocked'
  | 'cover-test-default'
  | 'cover-test-confirmation'
  | 'cover-test-formative'
  | 'cover-test-formative-late'
  | 'image-layout-progress'
  | 'cover-card-quizz'
  | 'cover-card-task'
  | 'cover-card-videolesson'
  | 'cover-card-quizz-done'
  | 'cover-card-task-done'
  | 'cover-card-audio'
  | 'cover-card-doc'
  | 'cover-card-link'
  | 'cover-card-material-without-content'
  | 'cover-card-video'
  | 'cover-card-zip'
  | 'cover-card-out-of-time'
  | 'noMaterials'
  | 'noUnitProgram'
  | 'barMenuLogo'
  | 'logoWhite'
  | 'defaultUser'
  | 'noPosts';

export type AssetImageProps = Readonly<
  Omit<ImageProps, 'src'> & {
    id?: AssetImageId;
    src?: string;
  }
>;

function AssetImage({ id = 'logoBig', src, ...rest }: AssetImageProps): JSX.Element {
  return <Image {...rest} src={src ?? CoreImage.getImage(id)} />;
}

export default AssetImage;
